/**
 * External-link exit notice.
 *
 * Port of the previous fullscreen_notice.js onto the shared modal engine. The design and the
 * decision logic are unchanged - same markup classes, same copy, same Cancel / Proceed
 * behaviour - but it now gets Esc, a focus trap and dialog semantics, none of which the
 * hand-rolled overlay had.
 *
 * Externality is decided from the browser-resolved URL (protocol + hostname) rather than by
 * pattern-matching the raw href. The original tested `^https?://` against the attribute, which
 * silently skipped protocol-relative `//host/path` links - they are external, but never warned.
 * Links carrying `noexitnotice` are still skipped, as before.
 */

interface EsrbGlobals {
	exit_notice_title?: string;
	exit_notice_message?: string;
	spanish?: boolean;
}

interface EsrbModalGlobalNotice {
	isSupported(): boolean;
	open( target: string | HTMLDialogElement, trigger?: HTMLElement | null ): boolean;
	close( dialog?: HTMLDialogElement | null ): void;
	create( content: Node, variant?: string ): HTMLDialogElement;
	closeButton(): HTMLButtonElement;
}

(function () {
	'use strict';

	function engine(): EsrbModalGlobalNotice | null {
		var api = ( window as unknown as { EsrbModal?: EsrbModalGlobalNotice } ).EsrbModal;

		return api && api.isSupported() ? api : null;
	}

	function settings(): EsrbGlobals {
		return ( window as unknown as { ESRB?: EsrbGlobals } ).ESRB || {};
	}

	/** The ACF message is an underscore template using currentSite / targetSite. */
	function renderMessage( currentSite: string, targetSite: string ): string {
		var raw = settings().exit_notice_message || '';
		var underscore = ( window as unknown as { _?: { template( s: string ): ( d: unknown ) => string } } )._;

		if ( !underscore ) {
			return raw;
		}

		try {
			return underscore.template( raw )( { currentSite: currentSite, targetSite: targetSite } );
		} catch ( error ) {
			return raw;
		}
	}

	function buildPanel( targetUrl: string, currentSite: string, targetSite: string ): DocumentFragment {
		var spanish = !!settings().spanish;
		var frag = document.createDocumentFragment();

		var close = document.createElement( 'a' );

		close.href = '#';
		close.className = 'close';
		close.setAttribute( 'data-modal-close', '' );
		close.setAttribute( 'aria-label', spanish ? 'Cerrar' : 'Close' );
		frag.appendChild( close );

		var heading = document.createElement( 'div' );

		heading.className = 'heading';
		frag.appendChild( heading );

		var content = document.createElement( 'div' );

		content.className = 'content';

		var title = document.createElement( 'div' );

		title.className = 'title';
		title.textContent = settings().exit_notice_title || '';
		content.appendChild( title );

		var message = document.createElement( 'div' );

		message.className = 'message';
		// Editor-authored HTML from ACF, same as the original implementation.
		message.innerHTML = renderMessage( currentSite, targetSite );
		content.appendChild( message );

		frag.appendChild( content );

		var buttons = document.createElement( 'div' );

		buttons.className = 'buttons';

		var cancel = document.createElement( 'a' );

		cancel.href = '#';
		cancel.className = 'button cancel';
		cancel.setAttribute( 'data-modal-close', '' );
		cancel.textContent = spanish ? 'Cancelar' : 'Cancel';
		buttons.appendChild( cancel );

		var proceed = document.createElement( 'a' );

		proceed.href = '#';
		proceed.className = 'button button-primary submit';
		proceed.setAttribute( 'data-url', targetUrl );
		proceed.textContent = spanish ? 'Continuar' : 'Proceed';
		buttons.appendChild( proceed );

		frag.appendChild( buttons );

		return frag;
	}

	document.addEventListener( 'click', function ( event: MouseEvent ) {
		var origin = event.target;

		if ( !( origin instanceof Element ) ) {
			return;
		}

		var link = origin.closest<HTMLAnchorElement>( 'a' );

		if ( !link ) {
			return;
		}

		// An <a> with no href (e.g. a bare anchor target) is not a link out of anywhere.
		if ( !link.getAttribute( 'href' ) ) {
			return;
		}

		// link.protocol / link.hostname are resolved by the browser, so `//host/path`,
		// `/path`, `#anchor`, `mailto:` and `tel:` are all classified correctly.
		if ( 'http:' !== link.protocol && 'https:' !== link.protocol ) {
			return;
		}

		if ( link.hostname === window.location.hostname ) {
			return;
		}

		if ( link.classList.contains( 'noexitnotice' ) ) {
			return;
		}

		var api = engine();

		if ( !api ) {
			return;
		}

		event.preventDefault();

		var gaCategory = link.getAttribute( 'data-ga-category' );
		var gaAction = link.getAttribute( 'data-ga-action' );
		var gaLabel = link.getAttribute( 'data-ga-label' );

		// Resolved absolute URL, so protocol-relative links open correctly too.
		var targetUrl = link.href;
		var modal = api;

		var dialog = modal.create(
			buildPanel( targetUrl, window.location.hostname, link.hostname ),
			'modal--notice'
		);

		dialog.addEventListener( 'click', function ( clickEvent: MouseEvent ) {
			var clicked = clickEvent.target;

			if ( !( clicked instanceof Element ) || !clicked.closest( '.submit' ) ) {
				return;
			}

			clickEvent.preventDefault();

			var tracker = ( window as unknown as { ga?: ( ...args: unknown[] ) => void } ).ga;

			if ( gaCategory && gaAction && typeof tracker === 'function' ) {
				tracker( 'send', 'event', gaCategory, gaAction, gaLabel );
			}

			// Opened from inside the click handler so the popup blocker allows it, as before.
			var opened = window.open( targetUrl, '_blank' );

			if ( opened ) {
				opened.focus();
			}

			modal.close( dialog );
		} );

		modal.open( dialog, link );
	} );
})();
